import i18next from 'i18next'
import { useEffect, useState } from 'react'
import { getCurrentLanguageCode } from './languages'

type Loader = () => Promise<{ default: Record<string, unknown> }>

// Ask Glow names buttons from the portal and the tracking pages too. Their catalogues stay in
// their own apps and arrive here as namespaces of their own, one language at a time, on demand.
// Tracking's English file brings its Norwegian and Swedish along, for two strings it shares
const CATALOGUES: Record<string, Record<string, Loader>> = {
  portal: {
    en: () => import('../../../customer-frontend/src/glow-lib/i18n-init/en'),
    nb: () => import('../../../customer-frontend/src/glow-lib/i18n-init/nb'),
    sv: () => import('../../../customer-frontend/src/glow-lib/i18n-init/sv'),
    dk: () => import('../../../customer-frontend/src/glow-lib/i18n-init/dk'),
    fi: () => import('../../../customer-frontend/src/glow-lib/i18n-init/fi'),
    fr: () => import('../../../customer-frontend/src/glow-lib/i18n-init/fr')
  },
  tracking: {
    en: () => import('../../../tracking/src/i18n/en'),
    nb: () => import('../../../tracking/src/i18n/nb'),
    sv: () => import('../../../tracking/src/i18n/sv'),
    // Tracking calls Danish "da"
    dk: () => import('../../../tracking/src/i18n/da'),
    fi: () => import('../../../tracking/src/i18n/fi'),
    fr: () => import('../../../tracking/src/i18n/fr')
  }
}

// Glow's own catalogue first, so a key two apps both hold reads as Glow shows it unless the
// quoted English text says otherwise
export const LABEL_NAMESPACES = ['translation', ...Object.keys(CATALOGUES)]

// One load per catalogue and language, however often the chat opens
const loads = new Map<string, Promise<void>>()

const loadCatalogue = (namespace: string, language: string): Promise<void> => {
  const load = CATALOGUES[namespace]?.[language]
  if (load === undefined) {
    return Promise.resolve()
  }
  const id = `${namespace}:${language}`
  const pending =
    loads.get(id) ??
    load()
      .then((module) => void i18next.addResourceBundle(language, namespace, module.default))
      // A chunk that failed to arrive (a deploy under an open tab) is asked for again next time;
      // the others still count, and its labels stay English, which is the fallback anyway
      .catch(() => {
        loads.delete(id)
      })
  loads.set(id, pending)
  return pending
}

// English as well as the user's language: the English text is how a label's app is recognised
export const loadOtherAppLabels = async (language: string): Promise<void> => {
  await Promise.all(
    Object.keys(CATALOGUES).flatMap((namespace) =>
      [...new Set(['en', language])].map((code) => loadCatalogue(namespace, code))
    )
  )
}

// Loads while the chat is open, and re-renders the answers already on screen once the words
// arrive. Glow switches language by reloading the page, so the language is read once per open
export const useOtherAppLabels = (active: boolean): number => {
  const [loaded, setLoaded] = useState(0)
  useEffect(() => {
    if (!active) {
      return
    }
    let cancelled = false
    loadOtherAppLabels(getCurrentLanguageCode()).then(() => !cancelled && setLoaded((count) => count + 1))
    return () => {
      cancelled = true
    }
  }, [active])
  return loaded
}
